import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';

interface Scene {
  id: string;
  number: number;
  title: string;
  description: string;
  duration: string;
  location: string;
  characters: string[];
  shotType: string;
  cameraMovement: string;
  lighting: string;
  sound: string;
  dialogue: string;
  notes: string;
}

const StoryboardPage: React.FC = () => {
  const [scenes, setScenes] = useState<Scene[]>([
    {
      id: '1',
      number: 1,
      title: '咖啡厅初遇',
      description: '小林疲惫地走进咖啡厅，遇到神秘的咖啡师阿杰',
      duration: '0:30',
      location: '咖啡厅内',
      characters: ['小林', '阿杰'],
      shotType: '中景',
      cameraMovement: '推镜头',
      lighting: '温暖的自然光',
      sound: '轻柔的爵士乐',
      dialogue: '阿杰：今天想喝点什么？',
      notes: '营造温馨浪漫的氛围'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingScene, setEditingScene] = useState<Scene | null>(null);
  const [newScene, setNewScene] = useState<Partial<Scene>>({
    title: '',
    description: '',
    duration: '',
    location: '',
    characters: [],
    shotType: '中景',
    cameraMovement: '固定',
    lighting: '自然光',
    sound: '',
    dialogue: '',
    notes: ''
  });

  const handleAddScene = () => {
    if (newScene.title && newScene.description) {
      const scene: Scene = {
        id: Date.now().toString(),
        number: scenes.length + 1,
        title: newScene.title || '',
        description: newScene.description || '',
        duration: newScene.duration || '',
        location: newScene.location || '',
        characters: newScene.characters || [],
        shotType: newScene.shotType || '中景',
        cameraMovement: newScene.cameraMovement || '固定',
        lighting: newScene.lighting || '自然光',
        sound: newScene.sound || '',
        dialogue: newScene.dialogue || '',
        notes: newScene.notes || ''
      };

      setScenes([...scenes, scene]);
      setNewScene({
        title: '',
        description: '',
        duration: '',
        location: '',
        characters: [],
        shotType: '中景',
        cameraMovement: '固定',
        lighting: '自然光',
        sound: '',
        dialogue: '',
        notes: ''
      });
      setShowAddForm(false);
    }
  };

  const handleEditScene = (scene: Scene) => {
    setEditingScene(scene);
    setNewScene({
      title: scene.title,
      description: scene.description,
      duration: scene.duration,
      location: scene.location,
      characters: scene.characters,
      shotType: scene.shotType,
      cameraMovement: scene.cameraMovement,
      lighting: scene.lighting,
      sound: scene.sound,
      dialogue: scene.dialogue,
      notes: scene.notes
    });
    setShowAddForm(true);
  };

  const handleUpdateScene = () => {
    if (editingScene && newScene.title && newScene.description) {
      const updatedScene: Scene = {
        ...editingScene,
        title: newScene.title || editingScene.title,
        description: newScene.description || editingScene.description,
        duration: newScene.duration || editingScene.duration,
        location: newScene.location || editingScene.location,
        characters: newScene.characters || editingScene.characters,
        shotType: newScene.shotType || editingScene.shotType,
        cameraMovement: newScene.cameraMovement || editingScene.cameraMovement,
        lighting: newScene.lighting || editingScene.lighting,
        sound: newScene.sound || editingScene.sound,
        dialogue: newScene.dialogue || editingScene.dialogue,
        notes: newScene.notes || editingScene.notes
      };

      setScenes(scenes.map(s => s.id === editingScene.id ? updatedScene : s));
      setEditingScene(null);
      setNewScene({
        title: '',
        description: '',
        duration: '',
        location: '',
        characters: [],
        shotType: '中景',
        cameraMovement: '固定',
        lighting: '自然光',
        sound: '',
        dialogue: '',
        notes: ''
      });
      setShowAddForm(false);
    }
  };

  const handleDeleteScene = (sceneId: string) => {
    if (confirm('确定要删除这个镜头吗？')) {
      setScenes(scenes.filter(s => s.id !== sceneId));
    }
  };

  const handleGenerateStoryboard = () => {
    // 模拟AI生成分镜
    const aiScenes: Scene[] = [
      {
        id: Date.now().toString(),
        number: scenes.length + 1,
        title: '月光下的承诺',
        description: '两人在月光下许下美好的约定',
        duration: '1:00',
        location: '咖啡厅露台',
        characters: ['小林', '阿杰'],
        shotType: '特写',
        cameraMovement: '环绕',
        lighting: '浪漫月光',
        sound: '轻柔的背景音乐',
        dialogue: '小林：我相信我们的未来会很美好。',
        notes: '营造浪漫的氛围，突出人物情感'
      },
      {
        id: (Date.now() + 1).toString(),
        number: scenes.length + 2,
        title: '清晨的咖啡厅',
        description: '新的一天开始，咖啡厅重新开门营业',
        duration: '0:45',
        location: '咖啡厅门口',
        characters: ['阿杰'],
        shotType: '全景',
        cameraMovement: '拉镜头',
        lighting: '晨光',
        sound: '开门声，清晨鸟鸣',
        dialogue: '阿杰：又是充满希望的一天。',
        notes: '展现新的一天开始的美好'
      }
    ];

    setScenes([...scenes, ...aiScenes]);
  };

  const getTotalDuration = () => {
    const total = scenes.reduce((sum, scene) => {
      const [minutes, seconds] = scene.duration.split(':').map(Number);
      return sum + minutes * 60 + seconds;
    }, 0);

    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 面包屑导航 */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-purple-600">首页</Link>
          <span>/</span>
          <span className="text-gray-900">分镜设计</span>
        </nav>

        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">分镜设计</h1>
          <p className="text-gray-600">专业的分镜头脚本设计，让您的短剧更加精彩</p>
          <div className="mt-4 flex items-center space-x-6 text-sm text-gray-600">
            <span>📹 总镜头数: {scenes.length}</span>
            <span>⏱️ 总时长: {getTotalDuration()}</span>
            <span>🎭 涉及角色: {Array.from(new Set(scenes.flatMap(s => s.characters))).length}</span>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
          >
            ➕ 手动添加镜头
          </button>
          <button
            onClick={handleGenerateStoryboard}
            className="px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
          >
            🎬 AI生成分镜
          </button>
          <Link
            to="/create"
            className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors text-center"
          >
            📝 返回剧本创作
          </Link>
          <button
            onClick={() => {
              if (scenes.length === 0) {
                alert('暂无分镜内容可以导出');
                return;
              }

              // 生成分镜脚本内容
              let storyboardContent = `AI短剧分镜脚本\n生成时间: ${new Date().toLocaleString()}\n\n`;
              storyboardContent += `总镜头数: ${scenes.length}\n总时长: ${getTotalDuration()}\n涉及角色: ${Array.from(new Set(scenes.flatMap(s => s.characters))).join(', ')}\n\n`;

              storyboardContent += '=' .repeat(50) + '\n\n';

              scenes.forEach((scene, index) => {
                storyboardContent += `镜头 ${scene.number}: ${scene.title}\n`;
                storyboardContent += `- 时长: ${scene.duration}\n`;
                storyboardContent += `- 地点: ${scene.location}\n`;
                storyboardContent += `- 镜头类型: ${scene.shotType}\n`;
                storyboardContent += `- 镜头运动: ${scene.cameraMovement}\n`;
                storyboardContent += `- 灯光: ${scene.lighting}\n`;
                storyboardContent += `- 声音: ${scene.sound}\n`;
                storyboardContent += `- 涉及角色: ${scene.characters.join(', ')}\n`;
                storyboardContent += `- 镜头描述: ${scene.description}\n`;
                if (scene.dialogue) {
                  storyboardContent += `- 关键对话: ${scene.dialogue}\n`;
                }
                if (scene.notes) {
                  storyboardContent += `- 备注: ${scene.notes}\n`;
                }
                storyboardContent += '\n' + '-'.repeat(30) + '\n\n';
              });

              // 创建下载链接
              const blob = new Blob([storyboardContent], { type: 'text/plain;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `AI短剧分镜脚本_${new Date().toISOString().slice(0, 10)}.txt`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);

              alert('分镜脚本已导出为文本文件！');
            }}
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            📥 导出分镜脚本
          </button>
        </div>

        {/* 添加镜头表单 */}
        {showAddForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {editingScene ? '编辑镜头' : '添加新镜头'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">镜头标题</label>
                <input
                  type="text"
                  value={newScene.title}
                  onChange={(e) => setNewScene({...newScene, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入镜头标题"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">时长</label>
                <input
                  type="text"
                  value={newScene.duration}
                  onChange={(e) => setNewScene({...newScene, duration: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="例: 0:30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">拍摄地点</label>
                <input
                  type="text"
                  value={newScene.location}
                  onChange={(e) => setNewScene({...newScene, location: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="请输入拍摄地点"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">涉及角色</label>
                <input
                  type="text"
                  value={newScene.characters?.join(', ')}
                  onChange={(e) => setNewScene({...newScene, characters: e.target.value.split(',').map(s => s.trim())})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="用逗号分隔多个角色"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">镜头类型</label>
                <select
                  value={newScene.shotType}
                  onChange={(e) => setNewScene({...newScene, shotType: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="特写">特写</option>
                  <option value="近景">近景</option>
                  <option value="中景">中景</option>
                  <option value="全景">全景</option>
                  <option value="远景">远景</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">镜头运动</label>
                <select
                  value={newScene.cameraMovement}
                  onChange={(e) => setNewScene({...newScene, cameraMovement: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="固定">固定</option>
                  <option value="推镜头">推镜头</option>
                  <option value="拉镜头">拉镜头</option>
                  <option value="摇镜头">摇镜头</option>
                  <option value="移镜头">移镜头</option>
                  <option value="环绕">环绕</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">镜头描述</label>
                <textarea
                  value={newScene.description}
                  onChange={(e) => setNewScene({...newScene, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-20 resize-none"
                  placeholder="请输入镜头描述"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">灯光要求</label>
                <input
                  type="text"
                  value={newScene.lighting}
                  onChange={(e) => setNewScene({...newScene, lighting: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="例: 温暖的自然光"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">声音效果</label>
                <input
                  type="text"
                  value={newScene.sound}
                  onChange={(e) => setNewScene({...newScene, sound: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="例: 轻柔的背景音乐"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">关键对话</label>
                <textarea
                  value={newScene.dialogue}
                  onChange={(e) => setNewScene({...newScene, dialogue: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-16 resize-none"
                  placeholder="请输入关键对话"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">备注</label>
                <textarea
                  value={newScene.notes}
                  onChange={(e) => setNewScene({...newScene, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-16 resize-none"
                  placeholder="请输入备注信息"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={editingScene ? handleUpdateScene : handleAddScene}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                {editingScene ? '确认更新' : '确认添加'}
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingScene(null);
                  setNewScene({
                    title: '',
                    description: '',
                    duration: '',
                    location: '',
                    characters: [],
                    shotType: '中景',
                    cameraMovement: '固定',
                    lighting: '自然光',
                    sound: '',
                    dialogue: '',
                    notes: ''
                  });
                }}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                取消
              </button>
            </div>
          </div>
        )}

        {/* 分镜列表 */}
        <div className="space-y-6">
          {scenes.map((scene) => (
            <div key={scene.id} className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    {scene.number}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{scene.title}</h3>
                    <p className="text-sm text-gray-600">⏱️ {scene.duration} | 📍 {scene.location}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditScene(scene)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    编辑
                  </button>
                  <button
                    onClick={() => handleDeleteScene(scene.id)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                  >
                    删除
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">镜头描述</h4>
                  <p className="text-sm text-gray-600">{scene.description}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">技术参数</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>📹 镜头类型: {scene.shotType}</p>
                    <p>🎥 镜头运动: {scene.cameraMovement}</p>
                    <p>💡 灯光: {scene.lighting}</p>
                    <p>🔊 声音: {scene.sound}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">角色与对话</h4>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-gray-600">涉及角色:</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {scene.characters.map((character, index) => (
                          <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                            {character}
                          </span>
                        ))}
                      </div>
                    </div>
                    {scene.dialogue && (
                      <div>
                        <p className="text-sm text-gray-600">关键对话:</p>
                        <p className="text-sm text-gray-800 italic">"{scene.dialogue}"</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {scene.notes && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">备注</h4>
                  <p className="text-sm text-gray-600">{scene.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 时间轴预览 */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">时间轴预览</h3>
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="flex items-center space-x-2 overflow-x-auto">
              {scenes.map((scene, index) => (
                <div key={scene.id} className="flex-shrink-0">
                  <div className="w-20 h-12 bg-purple-600 rounded-lg flex items-center justify-center text-white text-xs font-bold mb-1">
                    {scene.number}
                  </div>
                  <p className="text-xs text-gray-600 text-center">{scene.duration}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryboardPage;